/** 認証トークン付きの fetch ラッパー（401 時に自動リフレッシュ） */

import { getIdToken, logout, refreshTokens, restoreIdToken } from "./auth";
import { logger } from "./logger";

const BASE_URL = "/api";

/** API エラー（Error 拡張のため class 使用） */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiSuccessResponse<T> = {
  success: true;
  data: T;
  meta?: { total: number };
};

type ApiErrorResponse = {
  success: false;
  error: string;
};

type ApiRawResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

const doFetch = async <T>(
  path: string,
  options?: RequestInit,
): Promise<{ data: T; meta?: { total: number } }> => {
  await restoreIdToken();
  const token = getIdToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  const json = (await res.json()) as ApiRawResponse<T>;

  if (!json.success) {
    throw new ApiError(json.error, res.status);
  }

  return { data: json.data, meta: json.meta };
};

const request = async <T>(
  path: string,
  options?: RequestInit,
): Promise<{ data: T; meta?: { total: number } }> => {
  try {
    return await doFetch<T>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const refreshed = (await refreshTokens()) === "refreshed";
      if (refreshed) {
        try {
          return await doFetch<T>(path, options);
        } catch (retryError) {
          if (retryError instanceof ApiError) {
            logger.error("API error after retry", {
              message: retryError.message,
              status: retryError.status,
            });
            throw retryError;
          }
        }
      }
      // unreachable でも logout() して refresh_token を消している。頻度が低いため見送り:
      // https://github.com/galactic1969/payload-alpha/issues/177
      logout();
      throw error;
    }
    if (error instanceof ApiError) {
      logger.error("API error", {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
    logger.error("Network error", error);
    throw new ApiError("通信エラーが発生しました");
  }
};

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
